import { defineStore, acceptHMRUpdate } from 'pinia'
import { DeviceService } from '@/api/client'
import { Task } from '@/classes/Task'
import handleRequest from '@/helpers/handleRequest'

type TState = {
  [key: string]: Array<Task>
}

const initialState: TState = {}

const defaultTasks: Array<Task> = [
  new Task(Task.id.CLOCKIN, 'Arbeitszeit', false),
  new Task(Task.id.BREAK, 'Pause', false),
]

const useTaskStore = defineStore('taskStore', {
  state: () => {
    return { employees: { ...initialState } }
  },
  getters: {
    taskOfEmployeeById(): (
      employee_id: number,
      taskId: number,
    ) => Task | undefined {
      return (employee_id: number, taskId: number) => {
        const tasksOfEmployee = this.tasksOfEmployee(employee_id)
        return tasksOfEmployee.find((task) => task.task_id === taskId)
      }
    },
    tasksOfEmployee: (state) => {
      return (employee_id: number) => {
        if (
          state.employees[employee_id] &&
          state.employees[employee_id].length > 0
        ) {
          return state.employees[employee_id]
        }
        return defaultTasks
      }
    },
    specialTasksOfEmployee: (state) => {
      return (employee_id: number) => {
        // tasks that are not default tasks
        return state.employees[employee_id].filter((task) =>
          defaultTasks.map((t) => t.task_id).includes(task.task_id),
        )
      }
    },
  },
  actions: {
    async sync() {
      const newState = { employees: { ...initialState } }

      const taskConfigs = (await handleRequest(DeviceService.getTaskConfigs()))
        ?.data
      if (!taskConfigs) return
      if (
        Array.isArray(taskConfigs) &&
        taskConfigs[0] &&
        taskConfigs[0].tasks
      ) {
        for (const task of taskConfigs) {
          if (!task.employee_id || !task.tasks) {
            continue
          }
          newState.employees[task.employee_id] = task.tasks.map(
            (task) => new Task(task.task_id, task.name, task.name_is_custom),
          )
        }
      }
      this.$state = newState
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (!isNaN(+key) && value.length) {
          return value.map(
            (taskString: Task) =>
              new Task(
                taskString.task_id,
                taskString.name,
                taskString.nameIsCustom,
              ),
          )
        } else {
          return value
        }
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTaskStore, import.meta.hot))
}

export { useTaskStore, defaultTasks }
